// Schlanker Hook ohne React Query (dependency-Diet). Liefert
// Liste + Refresh + lokale Mutations. Wird in Phase 4+ ggf. auf React Query gehoben.

import { useCallback, useEffect, useState } from "react";
import { type BoardDto, type ListStatus, boardsApi } from "./api.js";

export interface UseBoardsState {
  boards: BoardDto[];
  loading: boolean;
  error: string | null;
  status: ListStatus;
  setStatus: (s: ListStatus) => void;
  refresh: () => Promise<void>;
  createBoard: (input: { name: string; methodTemplate: string | null }) => Promise<BoardDto>;
  renameBoard: (id: string, name: string) => Promise<BoardDto>;
  archiveBoard: (id: string) => Promise<BoardDto>;
}

export function useBoards(initialStatus: ListStatus = "active"): UseBoardsState {
  const [boards, setBoards] = useState<BoardDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<ListStatus>(initialStatus);

  const fetchBoards = useCallback(async (s: ListStatus): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const next = await boardsApi.list(s);
      setBoards(next);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load boards");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchBoards(status);
  }, [status, fetchBoards]);

  const refresh = useCallback(() => fetchBoards(status), [fetchBoards, status]);

  const createBoard = useCallback(
    async (input: { name: string; methodTemplate: string | null }): Promise<BoardDto> => {
      const board = await boardsApi.create(input);
      // Eigenes Board passt zur aktiven Liste → optimistisch einfuegen.
      if (status === "active" || status === "all") {
        setBoards((prev) => [board, ...prev]);
      }
      return board;
    },
    [status],
  );

  const renameBoard = useCallback(async (id: string, name: string): Promise<BoardDto> => {
    const board = await boardsApi.rename(id, name);
    setBoards((prev) => prev.map((b) => (b.id === id ? board : b)));
    return board;
  }, []);

  const archiveBoard = useCallback(
    async (id: string): Promise<BoardDto> => {
      const board = await boardsApi.archive(id);
      if (status === "active") {
        setBoards((prev) => prev.filter((b) => b.id !== id));
      } else {
        setBoards((prev) => prev.map((b) => (b.id === id ? board : b)));
      }
      return board;
    },
    [status],
  );

  return {
    boards,
    loading,
    error,
    status,
    setStatus,
    refresh,
    createBoard,
    renameBoard,
    archiveBoard,
  };
}
